import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

// Enums matching the backend types
export enum CaseCategory {
  HIGH_VALUE = 'high_value',
  FULL_POLICY_LIMITS = 'full_policy_limits',
  SOFT_TISSUE = 'soft_tissue',
}

export enum DemandType {
  FIRST_PARTY = '1P',
  THIRD_PARTY = '3P',
  MEDPAY = 'medpay',
  PIP = 'pip',
}

export enum MostSignificantCare {
  CONSERVATIVE = 'conservative',
  SURGICAL_RECOMMENDATION = 'surgical_recommendation',
  SURGICAL_PROCEDURE = 'surgical_procedure',
}

// DTO matching the backend Pydantic schema
export interface DemandReadiness {
  bills_records_confirmed: boolean;
  case_category: CaseCategory;
  demand_type: DemandType;
  most_significant_care: MostSignificantCare;
  special_instructions?: string | null;
}

interface DemandReadinessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: DemandReadiness) => Promise<void>;
  matterName: string;
}

const CASE_CATEGORY_LABELS: Record<CaseCategory, string> = {
  [CaseCategory.HIGH_VALUE]: 'High Value',
  [CaseCategory.FULL_POLICY_LIMITS]: 'Full Policy Limits',
  [CaseCategory.SOFT_TISSUE]: 'Soft Tissue',
};

const DEMAND_TYPE_LABELS: Record<DemandType, string> = {
  [DemandType.FIRST_PARTY]: '1P (First Party / UM/UIM)',
  [DemandType.THIRD_PARTY]: '3P (Third Party Liability)',
  [DemandType.MEDPAY]: 'Medpay',
  [DemandType.PIP]: 'PIP (Personal Injury Protection)',
};

const CARE_LABELS: Record<MostSignificantCare, string> = {
  [MostSignificantCare.CONSERVATIVE]: 'Conservative',
  [MostSignificantCare.SURGICAL_RECOMMENDATION]: 'Surgical Recommendation',
  [MostSignificantCare.SURGICAL_PROCEDURE]: 'Surgical Procedure',
};

/**
 * Modal for collecting demand readiness information when transitioning
 * a matter to Ready for Demand status.
 */
export const DemandReadinessModal: React.FC<DemandReadinessModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  matterName,
}) => {
  const [billsRecordsConfirmed, setBillsRecordsConfirmed] =
    React.useState(false);
  const [caseCategory, setCaseCategory] = React.useState<CaseCategory | ''>('');
  const [demandType, setDemandType] = React.useState<DemandType | ''>('');
  const [mostSignificantCare, setMostSignificantCare] = React.useState<
    MostSignificantCare | ''
  >('');
  const [specialInstructions, setSpecialInstructions] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const canSubmit =
    billsRecordsConfirmed &&
    caseCategory !== '' &&
    demandType !== '' &&
    mostSignificantCare !== '';

  // Reset form when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setBillsRecordsConfirmed(false);
      setCaseCategory('');
      setDemandType('');
      setMostSignificantCare('');
      setSpecialInstructions('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      await onConfirm({
        bills_records_confirmed: billsRecordsConfirmed,
        case_category: caseCategory as CaseCategory,
        demand_type: demandType as DemandType,
        most_significant_care: mostSignificantCare as MostSignificantCare,
        special_instructions: specialInstructions.trim() || null,
      });
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Handle Cmd/Ctrl+Enter to submit
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (
      e.key === 'Enter' &&
      (e.metaKey || e.ctrlKey) &&
      canSubmit &&
      !isSubmitting
    ) {
      e.preventDefault();
      handleConfirm();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-lg" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle>Ready for Demand</DialogTitle>
          <DialogDescription>
            Please provide the following information before transitioning
            &ldquo;{matterName}&rdquo; to Ready for Demand.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Bills/Records Confirmation */}
          <div className="flex items-start space-x-3">
            <Checkbox
              id="bills-records-confirmed"
              checked={billsRecordsConfirmed}
              onCheckedChange={(checked) =>
                setBillsRecordsConfirmed(checked === true)
              }
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor="bills-records-confirmed"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                All bills and records received{' '}
                <span className="text-red-500">*</span>
              </Label>
              <p className="text-xs text-muted-foreground">
                I confirm that all bills and records are received and in file.
              </p>
            </div>
          </div>

          {/* Case Category */}
          <div className="space-y-2">
            <Label htmlFor="case-category">
              Case Category <span className="text-red-500">*</span>
            </Label>
            <Select
              value={caseCategory}
              onValueChange={(value) => setCaseCategory(value as CaseCategory)}
            >
              <SelectTrigger id="case-category">
                <SelectValue placeholder="Select case category" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CASE_CATEGORY_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Demand Type */}
          <div className="space-y-2">
            <Label htmlFor="demand-type">
              Demand Type <span className="text-red-500">*</span>
            </Label>
            <Select
              value={demandType}
              onValueChange={(value) => setDemandType(value as DemandType)}
            >
              <SelectTrigger id="demand-type">
                <SelectValue placeholder="Select demand type" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(DEMAND_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Most Significant Care */}
          <div className="space-y-2">
            <Label htmlFor="most-significant-care">
              Most Significant Care Received{' '}
              <span className="text-red-500">*</span>
            </Label>
            <Select
              value={mostSignificantCare}
              onValueChange={(value) =>
                setMostSignificantCare(value as MostSignificantCare)
              }
            >
              <SelectTrigger id="most-significant-care">
                <SelectValue placeholder="Select most significant care" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CARE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Special Instructions */}
          <div className="space-y-2">
            <Label htmlFor="special-instructions">
              Special Circumstances / Instructions
            </Label>
            <Textarea
              id="special-instructions"
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              placeholder="Enter any special circumstances or instructions for the demand..."
              rows={3}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
